import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Film, ChevronLeft, Clapperboard } from "lucide-react";
import toast from "react-hot-toast";
import { listShows, listMovies } from "../../api/movieKeys";
import { unwrapList, errorMessage, formatDate } from "../../lib/records";

export default function MovieShows() {
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movies, setMovies] = useState([]);
  const [movieFilter, setMovieFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const loadMovies = useCallback(async () => {
    try {
      const res = await listMovies({ limit: 100 });
      setMovies(unwrapList(res).items);
    } catch { /* non-fatal */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listShows({
        ...(movieFilter ? { movie: movieFilter } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      });
      const { items } = unwrapList(res);
      setShows(items);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [movieFilter, from, to]);

  useEffect(() => { loadMovies(); }, [loadMovies]);
  useEffect(() => { load(); }, [load]);

  const totalShows = shows.reduce((s, r) => s + (r.shows ?? 1), 0);
  const totalKeys = shows.reduce((s, r) => s + (r.keys ?? 0), 0);
  const hasFilters = movieFilter || from || to;

  return (
    <div className="space-y-5 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/cinema")}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
        >
          <ChevronLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Clapperboard size={20} className="text-emerald-600" />
            Shows Report
          </h1>
          <p className="text-sm text-gray-500">Screenings grouped by date and film</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={movieFilter}
          onChange={e => setMovieFilter(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
        >
          <option value="">All films</option>
          {movies.map(m => <option key={m._id} value={m._id}>{m.title}</option>)}
        </select>
        <input
          type="date"
          value={from}
          onChange={e => setFrom(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
        />
        <span className="text-gray-400 text-sm">to</span>
        <input
          type="date"
          value={to}
          onChange={e => setTo(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
        />
        {hasFilters && (
          <button
            onClick={() => { setMovieFilter(""); setFrom(""); setTo(""); }}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 transition"
          >
            Clear
          </button>
        )}
      </div>

      {/* Summary totals */}
      {!loading && shows.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-4">
            <p className="text-xs text-gray-500 mb-1">Total Shows</p>
            <p className="text-2xl font-bold text-emerald-600">{totalShows}</p>
          </div>
          <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-4">
            <p className="text-xs text-gray-500 mb-1">Keys Consumed</p>
            <p className="text-2xl font-bold text-amber-600">{totalKeys}</p>
          </div>
        </div>
      )}

      {/* Shows list */}
      <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3].map(i => <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse" />)}
          </div>
        ) : shows.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Film size={28} className="mx-auto mb-2 opacity-40" />
            <p>No shows found for this period</p>
            <p className="text-xs mt-1">Shows appear here after keys are consumed.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {shows.map((row, i) => (
              <div key={i} className="p-4 flex flex-wrap items-center gap-3 hover:bg-gray-50/60 transition">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{row.movieTitle ?? "—"}</p>
                  <p className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                    <span>{formatDate(row.date)}</span>
                    {row.screens && (
                      <span>{row.screens} screen{row.screens !== 1 ? "s" : ""}</span>
                    )}
                    {row.firstShowAt && <span>First: {row.firstShowAt}</span>}
                    {row.lastShowAt && <span>Last: {row.lastShowAt}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-6 shrink-0">
                  <div className="text-center">
                    <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Shows</p>
                    <p className="text-lg font-bold text-emerald-600">{row.shows ?? 1}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Keys</p>
                    <p className="text-lg font-bold text-amber-600">{row.keys ?? 0}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
